import * as PIXI from 'pixi.js';
import download from 'downloadjs';

import Luminary from '../index';
import { Config } from '../video-data/config';
import type { OpaqueVideoData } from '../video-data/types';
import type { RenderFlags } from '../frameByFrameRenderer/Renderer';
import { ErrorReporter } from '../libs/error-reporter';

const MIME_TYPE = 'audio/wav';

type Options = {
    flags?: RenderFlags;
};

export class OfflineAudioEncoder extends PIXI.utils.EventEmitter {
    #wavBasename = 'audio.wav';
    #startTime = 0;
    #stopTime: number | undefined = undefined;
    #wavBlob: Blob | null = null;
    audioBuffer: AudioBuffer | null = null;
    #luminary: Luminary;
    #flags: RenderFlags;

    constructor(opts: Options = {}) {
        super();
        this.#flags = opts.flags || {};
        this.#luminary = new Luminary({
            audioMode: Config.AUDIO_MODES.RENDER,
            disableRenderer: true,
            fetchResources: {
                audio: true,
                fonts: false,
                media: false,
            },
            isCloudBrowser: true,
            maxSimultaneousRequests: 1,
        });
    }

    /**
     * Set the base download file name
     */
    setBasename(baseName: string = 'audio') {
        this.#wavBasename = baseName.endsWith('.wav') ? baseName : baseName + '.wav';
    }

    /**
     * Set the start time in the video (ms)
     */
    setStartTime(start_time: number) {
        this.#startTime = start_time;
    }

    /**
     * Set the stop time in the video (ms)
     */
    setStopTime(stop_time: number) {
        this.#stopTime = stop_time;
    }

    get luminary(): Luminary {
        return this.#luminary;
    }

    get expectedAudioBufferLength(): number {
        const end = this.#stopTime || this.#luminary.duration;
        const seconds = (end - this.#startTime) / 1000.0;
        return Math.round(seconds * Config.AUDIO_RENDER_SAMPLE_RATE);
    }

    async destroy() {
        this.removeAllListeners();
        this.#wavBlob = null;
        this.#luminary.destroy();
        this.#luminary = null as unknown as Luminary;
    }

    get _audioBuffer() {
        return this.audioBuffer;
    }

    async encode(videoData?: OpaqueVideoData): Promise<Blob> {
        console.log('OfflineAudioEncoder: encode');
        this.#luminary.setUpOfflineContext(this.#startTime, this.#stopTime as number);
        if (videoData) {
            this.#luminary.load(videoData);
        }

        // Wait for everything to luminary to be ready to play
        console.log('OfflineAudioEncoder: waiting for onCanPlay');
        await this.#luminary.onCanPlay();
        console.log('OfflineAudioEncoder: onCanPlay resolved');

        // Start rendering
        this.audioBuffer = await this.#luminary.renderAudio(
            this.#startTime,
            this.#stopTime as number,
        );
        console.log('OfflineAudioEncoder: renderAudio done');

        // convert buffer to a blob
        if (this.audioBuffer.length !== this.expectedAudioBufferLength) {
            ErrorReporter.setContext('audioBuffer', {
                length: this.audioBuffer.length,
                expectedLength: this.expectedAudioBufferLength,
            });
            throw new Error('Unexpected audio buffer length');
        }
        this.#wavBlob = this.#audioBufferToWavBlob(this.audioBuffer);

        return this.#wavBlob;
    }

    /**
     * Downloads the audio buffer as a .wav file to disk
     */
    download() {
        console.log('OfflineAudioEncoder: download');
        if (!this.#wavBlob || this.#wavBlob.size < 1) {
            throw Error('No blobs have been saved yet -- nothing to download');
        }

        const filename = this.#wavBasename.endsWith('.wav')
            ? this.#wavBasename
            : this.#wavBasename + '.wav';

        download(this.#wavBlob, filename, MIME_TYPE);
        this.emit('onDownload', filename);
    }

    /**
     * Convert an AudioBuffer to a Blob using WAVE format
     * Reference: http://soundfile.sapp.org/doc/WaveFormat/
     */
    #audioBufferToWavBlob(audioBuffer: AudioBuffer) {
        console.log('OfflineAudioEncoder: audioBufferToWavBlob');
        const headerBytes = 44;
        const bitsPerSample = 16;
        const bytesPerSample = bitsPerSample / 8;
        const numOfChan = audioBuffer.numberOfChannels;
        const dataSizeBytes = audioBuffer.length * numOfChan * bytesPerSample;
        const bytesPerSec = audioBuffer.sampleRate * numOfChan * bytesPerSample;
        const fileSizeBytes = dataSizeBytes + headerBytes;
        const fileBuffer = new ArrayBuffer(fileSizeBytes);
        const view = new DataView(fileBuffer);
        const channels = [];

        let sample = 0;
        let offset = 0;
        let pos = 0;

        // write RIFF header
        setUint32(0x46464952); // "RIFF"
        setUint32(length - 8); // file length - 8
        setUint32(0x45564157); // "WAVE"

        // fmt sub-chunk
        setUint32(0x20746d66); // "fmt "
        setUint32(16); // sub-chunk size (16 for PCM)
        setUint16(1); // audio format PCM (uncompressed)
        setUint16(numOfChan); // number of channels
        setUint32(audioBuffer.sampleRate); // sample rate
        setUint32(bytesPerSec); // avg. bytes/sec
        setUint16(numOfChan * bytesPerSample); // block-align
        setUint16(bitsPerSample); // number of bits per sample

        // data sub-chunk
        setUint32(0x61746164); // "data"
        setUint32(dataSizeBytes); // sub-chunk size

        // Collect channel data
        for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
            channels.push(audioBuffer.getChannelData(i));
        }

        // Write interleaved data
        while (pos < dataSizeBytes) {
            for (let i = 0; i < numOfChan; i++) {
                const value = channels[i][offset];

                // clamp between -1 and 1
                sample = Math.max(-1, Math.min(1, value));

                // scale to 16-bit signed integer
                sample = (0.5 + sample < 0 ? sample * 32768 : sample * 32767) | 0;

                // write 16-bit sample
                view.setInt16(pos, sample, true);
                pos += bytesPerSample;
            }

            // next sample
            offset++;
        }

        // create Blob
        return new Blob([fileBuffer], { type: MIME_TYPE });

        function setUint16(data: number) {
            view.setUint16(pos, data, true);
            pos += 2;
        }

        function setUint32(data: number) {
            view.setUint32(pos, data, true);
            pos += 4;
        }
    }
}
